import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import type { SubscriptionTier, SessionUsage } from '../types';
import { FREE_LIMITS } from '../types';

const SESSION_STORAGE_KEY = 'emdr_session_usage';

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function readSessionUsage(): SessionUsage {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) return { month: getCurrentMonth(), count: 0 };
    const parsed = JSON.parse(stored) as SessionUsage;
    if (parsed.month !== getCurrentMonth()) return { month: getCurrentMonth(), count: 0 };
    return parsed;
  } catch {
    return { month: getCurrentMonth(), count: 0 };
  }
}

function writeSessionUsage(usage: SessionUsage): void {
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(usage));
  } catch {
    // Ignore storage errors (private browsing, quota exceeded)
  }
}

interface FreemiumContextValue {
  tier: SubscriptionTier;
  trialDaysLeft: number | null;
  portalUrl: string | null;
  isLoading: boolean;
  isPro: boolean;
  isTrial: boolean;
  isFree: boolean;
  sessionsUsedThisMonth: number;
  sessionsRemaining: number;
  tryCountSession: () => 'counted' | 'limit_reached';
  checkSubscription: () => Promise<void>;
}

const FreemiumContext = createContext<FreemiumContextValue | undefined>(undefined);

export function FreemiumProvider({ children }: { children: ReactNode }) {
  const { authState, user } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>('free');
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null);
  const [portalUrl, setPortalUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionsUsedThisMonth, setSessionsUsedThisMonth] = useState(() => readSessionUsage().count);

  const checkSubscription = useCallback(async (): Promise<void> => {
    if (authState === 'loading') return;

    setIsLoading(true);

    try {
      if (authState === 'signed-out' || !user) {
        setTier('free');
        setTrialDaysLeft(null);
        setPortalUrl(null);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('subscription-status');

      if (error) {
        console.error('Failed to fetch subscription status:', error);
        setTier('free');
        setTrialDaysLeft(null);
        setPortalUrl(null);
        return;
      }

      const subscriptionData = data as {
        tier: SubscriptionTier;
        trialDaysLeft: number | null;
        trialExpired?: boolean;
        portalUrl?: string | null;
      };

      if (subscriptionData.trialExpired) {
        setTier('free');
        setTrialDaysLeft(null);
        setPortalUrl(null);
      } else {
        setTier(subscriptionData.tier || 'free');
        setTrialDaysLeft(subscriptionData.trialDaysLeft);
        setPortalUrl(subscriptionData.portalUrl || null);
      }

      console.log(`Subscription tier: ${subscriptionData.tier} (user: ${user?.email})`);
    } catch (error) {
      console.error('Failed to check subscription:', error);
      setTier('free');
      setTrialDaysLeft(null);
      setPortalUrl(null);
    } finally {
      setIsLoading(false);
    }
  }, [authState, user]);

  useEffect(() => {
    checkSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  const isPro = tier === 'pro' || tier === 'trial';

  const tryCountSession = useCallback((): 'counted' | 'limit_reached' => {
    if (isPro) return 'counted';
    const usage = readSessionUsage();
    if (usage.count >= FREE_LIMITS.monthlySessionLimit) return 'limit_reached';
    const newUsage: SessionUsage = { month: usage.month, count: usage.count + 1 };
    writeSessionUsage(newUsage);
    setSessionsUsedThisMonth(newUsage.count);
    return 'counted';
  }, [isPro]);

  const sessionsRemaining = isPro
    ? Infinity
    : Math.max(0, FREE_LIMITS.monthlySessionLimit - sessionsUsedThisMonth);

  const value: FreemiumContextValue = {
    tier,
    trialDaysLeft,
    portalUrl,
    isLoading,
    isPro,
    isTrial: tier === 'trial',
    isFree: tier === 'free',
    sessionsUsedThisMonth,
    sessionsRemaining,
    tryCountSession,
    checkSubscription,
  };

  return <FreemiumContext.Provider value={value}>{children}</FreemiumContext.Provider>;
}

export function useFreemium(): FreemiumContextValue {
  const context = useContext(FreemiumContext);
  if (context === undefined) {
    throw new Error('useFreemium must be used within a FreemiumProvider');
  }
  return context;
}
